import { Box, Chip, makeStyles, Paper, Typography } from "@material-ui/core";
import React from "react";
import { AddStaffButtonAndDialog } from "./AddStaffButtonAndDialog";
import { PermissionsList } from "./PermissionsList";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { usersService } from "../../../services";
import { RecipientOrg } from "../../../domain/RecipientsViewModel";
import { User } from "../../../domain/User";
import { ItemType } from "@ofqual/portal-components/components/types";
import { useDispatch } from "react-redux";
import { updateAoUsers } from "../../../redux/actions";
import { Skeleton } from "@material-ui/lab";

import { UserRoleText } from "../../../domain/userRole";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { UserType } from "../../../redux/types";
import { authSelector } from "../../../redux/selectors";

const useStyles = makeStyles((theme) => ({
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

export type PermissionsProps = {
  recipientOrg: RecipientOrg;
  id: string;
  roles: string[];
  allowReassignment: boolean;
};

export const Permissions: React.FC<PermissionsProps> = ({
  recipientOrg: { organisationRecipientId, id: orgId, users, closed },
  id,
  roles,
  allowReassignment,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [userLookups] = useLookup({
    service: usersService,
    id: "id",
    label: (item) => item.fullName ?? item.upn,
    params: { 
      awardingOrgId: [orgId], 
      userType: undefined,
      active: true,
    },
  });

  const typedUserLookps = (userLookups as unknown) as (User & ItemType)[];

  const handleAddUser = (newUser: { id: string; upn: string }) => {
    dispatch(
      updateAoUsers(id, organisationRecipientId, orgId, [...users, newUser])
    );
  };

  const handleRemoveUser = (userIdToRemove: string) => {
    dispatch(
      updateAoUsers(
        id,
        organisationRecipientId,
        orgId,
        users.filter((user) => user.id !== userIdToRemove)
      )
    );
  };

  const { userType } = useAuth(authSelector);
  return (
    <>
      <Typography variant="h6">Role-based Permissions</Typography>
      <Paper className={classes.formSection}>
        {roles.map((role) => (
          <Chip
            key={role}
            label={UserRoleText[+role]}
            color="primary"
            style={{ marginLeft: 5 }}
          />
        ))}
      </Paper>

      <Box display="flex" alignItems="center">
        <Box flexGrow={1}>
          <Typography variant="h6">Staff Permissions</Typography>
        </Box>

        <Box>
          {userType === UserType.AO && allowReassignment && (
            <AddStaffButtonAndDialog
              users={users}
              userLookups={typedUserLookps}
              onSelect={handleAddUser}
              disabled={closed}
            />
          )}
        </Box>
      </Box>
      <Paper className={classes.formSection}>
        {userLookups.isPlaceholder ? (
          <div>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
          </div>
        ) : (
          <PermissionsList
            users={users}
            userLookups={typedUserLookps}
            onDelete={handleRemoveUser}
            allowReassignment={allowReassignment}
          />
        )}
      </Paper>
    </>
  );
};
